import { useRef, useEffect, useCallback, useLayoutEffect, useState, type ReactNode } from 'react'
import type Hls from 'hls.js'
import {
  CaptionsIcon,
  Forward10Icon,
  PlayIcon,
  PauseIcon,
  Replay10Icon,
  VolumeOnIcon,
  VolumeOffIcon,
} from './VideoControls'
import { VideoProgressBar } from './VideoProgressBar'
import { PlaybackRateMenu } from './PlaybackRateMenu'
import { scopedVideoId, useVideoIdScope } from './videoIdScopeContext'
import { useVideoPlayback } from '../../hooks/useVideoPlayback'
import { SEEK_JUMP_SECONDS, jumpSeek, useIsSeekable } from '../../hooks/useVideoProgress'
import { usePressAndHold } from '../../hooks/usePressAndHold'
import { useControlsAutoHide } from '../../hooks/useControlsAutoHide'
import {
  HOLD_PLAYBACK_RATE,
  formatPlaybackRate,
  stepPlaybackRate,
  useVideoPlaybackRate,
} from '../../hooks/useVideoPlaybackRate'
import { useVideoCaptions, type ResolvedCaptionTrack } from '../../hooks/useVideoCaptions'
import { useSurfaceVideoContext } from '../../surfaces/surfaceVideoContextValue'
import { useFeedConfig } from '../../contexts/feedConfigContext'
import { useI18n } from '../../i18n'
import {
  getPosterFromHls,
  getVideoSrcWithPreview,
  isHlsUrl,
  resolveVideoMedia,
  type MediaItem,
  buildImageSrcSet,
  IMAGE_SIZES_ATTR,
} from './cardMediaUtils'

/**
 * The slice of `FeedMedia` this component needs (`resolveVideoMedia` also
 * accepts partial legacy media objects).
 */
interface CardMediaProps {
  media: MediaItem[]
  preload?: boolean
  postId: string
  decorative?: boolean
  onFirstPlay?: () => void
  onMuteToggle?: (mutedAfterToggle: boolean) => void
}

function MediaControlIcon({
  url,
  fallback,
  large = false,
}: {
  url: string
  fallback: ReactNode
  large?: boolean
}) {
  return url ? (
    <img
      className={`card-media-custom-icon${large ? ' card-media-custom-icon--large' : ''}`}
      src={url}
      alt=""
      aria-hidden="true"
    />
  ) : fallback
}

const NO_CAPTIONS: ResolvedCaptionTrack[] = []

/** Keyboard shortcuts on a focused player (YouTube's): j / l seek, c captions, < / > speed. */
type PlayerShortcut = 'back' | 'forward' | 'captions' | 'slower' | 'faster'

function playerShortcut(e: KeyboardEvent): PlayerShortcut | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null
  switch (e.key) {
    case 'j':
    case 'J':
      return 'back'
    case 'l':
    case 'L':
      return 'forward'
    case 'c':
    case 'C':
      return 'captions'
    case '<':
      return 'slower'
    case '>':
      return 'faster'
    default:
      return null
  }
}

function VideoPlayer({
  src,
  fallbackSrc,
  poster,
  captions = NO_CAPTIONS,
  preload: shouldPreload,
  videoId,
  decorative,
  onFirstPlay,
  onMuteToggle,
}: {
  src: string
  fallbackSrc?: string
  poster?: string
  captions?: ResolvedCaptionTrack[]
  preload: boolean
  videoId: string
  decorative: boolean
  onFirstPlay?: () => void
  onMuteToggle?: (mutedAfterToggle: boolean) => void
}) {
  const hlsRef = useRef<Hls | null>(null)
  const localVideoRef = useRef<HTMLVideoElement | null>(null)
  const localContainerRef = useRef<HTMLDivElement | null>(null)
  const surfaceContext = useSurfaceVideoContext()
  const { branding } = useFeedConfig()
  const { t } = useI18n()
  const {
    mediaPlayIconUrl = '',
    mediaPauseIconUrl = '',
    mediaMutedIconUrl = '',
  } = branding.assets || {}
  const {
    isPlaying,
    isActive,
    isMuted,
    togglePlay,
    toggleMute,
    setVideoElement,
    setContainerElement,
    setHlsInstance,
  } = useVideoPlayback(videoId, surfaceContext)

  // State as well as a ref: the controls auto-hide listeners attach to it.
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)

  const containerCallbackRef = useCallback((el: HTMLDivElement | null) => {
    localContainerRef.current = el
    setContainerEl(el)
    setContainerElement(el)
  }, [setContainerElement])

  // State, not a ref: the progress bar's subscription effect has to re-run if
  // the element is ever replaced. `setState` is stable, so the deps below do
  // not change.
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null)
  const [rateMenuOpen, setRateMenuOpen] = useState(false)
  const [scrubbing, setScrubbing] = useState(false)

  const combinedVideoRef = useCallback((el: HTMLVideoElement | null) => {
    localVideoRef.current = el
    setVideoEl(el)
    setVideoElement(el)
  }, [setVideoElement])

  useEffect(() => {
    setContainerElement(localContainerRef.current)
    setVideoElement(localVideoRef.current)
    return () => {
      setContainerElement(null)
      setVideoElement(null)
    }
  }, [setContainerElement, setVideoElement])

  useEffect(() => {
    const video = localVideoRef.current
    if (!video || !isHlsUrl(src)) return

    let cancelled = false
    const applyMp4Fallback = () => {
      if (cancelled || !fallbackSrc || !video) return
      hlsRef.current?.destroy()
      hlsRef.current = null
      setHlsInstance(null)
      video.src = getVideoSrcWithPreview(fallbackSrc)
      video.load()
      // Re-run the store's readiness handshake: setVideoElement triggers
      // resumeActivePlayback, which retries play() only if this video is
      // still the desired active, non-manually-paused video.
      setVideoElement(video)
    }
    const applyNativeOrMp4Fallback = () => {
      if (cancelled) return
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = src
        video.load()
        setVideoElement(video)
        return
      }
      applyMp4Fallback()
    }

    // canPlayType('application/vnd.apple.mpegurl') is not trustworthy on its
    // own: Chromium answers 'maybe' and then cannot play HLS, which sent every
    // Chrome, Edge and Android session straight to the MP4.
    //
    // ManagedMediaSource is WebKit-only, so it (or the absence of MSE entirely)
    // identifies the browsers whose native HLS claim is real. Those keep native
    // playback for hardware decoding, AirPlay and Picture in Picture; everyone
    // else goes through hls.js.
    //
    // This must be the full hls.js build, not `hls.js/dist/hls.light.mjs`.
    // The transcoder writes video-only renditions plus one alternate audio
    // rendition (`#EXT-X-MEDIA:TYPE=AUDIO`, see
    // infra/cloud-functions/create-transcode-job-hls), and the light build
    // omits the alternate-audio controllers, so it plays the video track with
    // no audio at all; unmuting then does nothing (#587).
    const hasNativeHlsClaim = video.canPlayType('application/vnd.apple.mpegurl') !== ''
    const isWebKitMediaStack = typeof window !== 'undefined'
      && ('ManagedMediaSource' in window || !window.MediaSource)

    if (hasNativeHlsClaim && isWebKitMediaStack) {
      video.src = src
      // A video activated before this effect ran (a story or floating
      // player that owns playback as it mounts) tried play() on an element
      // without a source, and assigning `src` cancels that attempt; let the
      // store retry now that there is something to play.
      setVideoElement(video)
    } else {
      import('hls.js').then(({ default: Hls }) => {
        if (cancelled) return
        if (!Hls.isSupported()) {
          applyNativeOrMp4Fallback()
          return
        }
        const hls = new Hls({
          // hls.js orders levels lowest→highest, so 0 is the 480p rung of the
          // canonical ladder (and the only rung of legacy single-rendition
          // manifests). Starting low and letting ABR up-switch cuts startup
          // bandwidth (#396); capLevelToPlayerSize still caps the ceiling.
          startLevel: 0,
          capLevelToPlayerSize: true,
          // Only the playing video loads segments: the store calls
          // startLoad/stopLoad as playback moves (setActiveVideo,
          // resumeActivePlayback). With the default autoStartLoad every
          // player on the page buffered from mount; the poster covers the
          // frame until playback starts.
          autoStartLoad: false,
          // maxBufferLength is only a floor: hls.js buffers up to
          // 8 × maxBufferSize (60 MB) / bitrate seconds, which at our ~0.5–2
          // Mbps ladder is the whole video, so maxMaxBufferLength is the real
          // forward-buffer cap.
          maxBufferLength: 10,
          maxMaxBufferLength: 20,
        })
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data?.fatal) applyMp4Fallback()
        })
        hls.loadSource(src)
        hls.attachMedia(video)
        hlsRef.current = hls
        setHlsInstance(hls)
      }).catch(applyNativeOrMp4Fallback)
    }

    return () => {
      cancelled = true
      hlsRef.current?.destroy()
      hlsRef.current = null
      setHlsInstance(null)
    }
  }, [fallbackSrc, src, setHlsInstance, setVideoElement])

  const hasPlayedRef = useRef(false)

  // A view is the viewer's first play *or* pause of this player (or an unmute
  // that starts it). Visibility autoplay (`recalculateMostVisible`) does not
  // count on its own, which is the legacy semantics: that widget only reported
  // the click, and a video that had already autoplayed was skipped there as
  // well. One deliberate widening: a viewer who pauses an autoplaying video is
  // counted here, since they demonstrably watched it. Counting autoplay itself
  // would report every card that scrolled into view, a different funnel from
  // the one RSI's dashboards were built on, so that stays a product decision
  // rather than a code one.
  const markViewed = useCallback(() => {
    if (hasPlayedRef.current) return
    hasPlayedRef.current = true
    onFirstPlay?.()
  }, [onFirstPlay])

  const handleTogglePlay = useCallback(() => {
    togglePlay()
    markViewed()
  }, [markViewed, togglePlay])

  const handlePlayButtonClick = useCallback((e: MouseEvent) => {
    // The surface's own onClick toggles too: stop here to avoid a double toggle.
    e.stopPropagation()
    handleTogglePlay()
  }, [handleTogglePlay])

  const handleToggleMute = useCallback((e: MouseEvent) => {
    e.stopPropagation()
    const started = toggleMute()
    onMuteToggle?.(!isMuted)
    if (started) markViewed()
  }, [isMuted, markViewed, onMuteToggle, toggleMute])

  // Decorative (background) videos get none of the interactive controls.
  const interactive = !decorative
  const seekable = useIsSeekable(videoEl)
  const hold = usePressAndHold({ enabled: interactive && isPlaying })
  // Decorative videos never follow the viewer's chosen speed: same gating as
  // captions below.
  const { rate, setRate } = useVideoPlaybackRate(interactive ? videoEl : null, hold.isHolding)
  const captionTracks = interactive ? captions : NO_CAPTIONS
  const {
    hasCaptions,
    enabled: captionsEnabled,
    toggle: toggleCaptions,
  } = useVideoCaptions(videoEl, captionTracks)
  const { consumeSuppressedClick } = hold
  const autoHide = useControlsAutoHide({
    element: containerEl,
    enabled: interactive,
    playing: isPlaying,
    locked: rateMenuOpen || scrubbing,
    tapReveals: surfaceContext !== 'vertical_stories',
  })
  const { consumeRevealTap } = autoHide

  const handleSurfaceClick = useCallback((e: MouseEvent) => {
    // Both read-and-clear flags are consumed every time, so neither outlives
    // the click it belongs to. Releasing a press-and-hold is not a tap (no
    // pause, no stories advance); a touch tap on hidden controls only brings
    // them back.
    const suppressed = consumeSuppressedClick()
    const revealTap = consumeRevealTap()
    if (suppressed || revealTap) {
      e.stopPropagation()
      return
    }
    handleTogglePlay()
  }, [consumeRevealTap, consumeSuppressedClick, handleTogglePlay])

  const handleSeekBack = useCallback((e: MouseEvent) => {
    e.stopPropagation()
    jumpSeek(localVideoRef.current, -SEEK_JUMP_SECONDS)
  }, [])

  const handleSeekForward = useCallback((e: MouseEvent) => {
    e.stopPropagation()
    jumpSeek(localVideoRef.current, SEEK_JUMP_SECONDS)
  }, [])

  const handleToggleCaptions = useCallback((e: MouseEvent) => {
    e.stopPropagation()
    toggleCaptions()
  }, [toggleCaptions])

  const handleSurfaceKeyDown = useCallback((e: KeyboardEvent) => {
    const shortcut = playerShortcut(e)
    if (!shortcut) return
    switch (shortcut) {
      case 'back':
      case 'forward':
        if (!jumpSeek(localVideoRef.current, shortcut === 'back' ? -SEEK_JUMP_SECONDS : SEEK_JUMP_SECONDS)) return
        break
      case 'captions':
        if (!hasCaptions) return
        toggleCaptions()
        break
      case 'slower':
      case 'faster':
        setRate(stepPlaybackRate(rate, shortcut === 'slower' ? -1 : 1))
        break
    }
    e.preventDefault()
    e.stopPropagation()
  }, [hasCaptions, rate, setRate, toggleCaptions])

  const handleVideoError = useCallback(() => {
    const video = localVideoRef.current
    if (!video || !fallbackSrc || video.src.includes(fallbackSrc)) return
    hlsRef.current?.destroy()
    hlsRef.current = null
    setHlsInstance(null)
    video.src = getVideoSrcWithPreview(fallbackSrc)
    video.load()
    // See applyMp4Fallback above: reattach through the store so
    // resumeActivePlayback decides whether to retry playback.
    setVideoElement(video)
  }, [fallbackSrc, setHlsInstance, setVideoElement])

  const videoPoster = poster || getPosterFromHls(src)
  const videoSrc = isHlsUrl(src) ? undefined : getVideoSrcWithPreview(src)

  return (
    <div
      ref={containerCallbackRef}
      className="card-media-video"
      onClick={interactive ? handleSurfaceClick : undefined}
      onKeyDown={interactive ? handleSurfaceKeyDown : undefined}
      {...(interactive ? hold.handlers : {})}
      aria-hidden={decorative || undefined}
      data-holding={hold.isHolding || undefined}
      data-controls-hidden={autoHide.shown ? undefined : 'true'}
      data-video-src={src}
      data-video-fallback-src={fallbackSrc}
    >
      <video
        ref={combinedVideoRef}
        src={videoSrc}
        poster={videoPoster}
        muted={isMuted}
        playsInline
        loop
        // A cross-origin <track> only loads from a CORS-mode video. Only opt
        // in when there are tracks, so caption-less videos keep plain
        // no-CORS fetches.
        crossOrigin={captionTracks.length > 0 ? 'anonymous' : undefined}
        preload={shouldPreload ? 'auto' : 'metadata'}
        onError={handleVideoError}
        className="card-media-video-element"
      >
        {captionTracks.map((track) => (
          <track
            key={track.url}
            kind={track.kind}
            src={track.url}
            srcLang={track.lang}
            label={track.label}
          />
        ))}
      </video>
      {interactive && hold.isHolding ? (
        <div className="card-media-rate-badge" role="status" aria-live="polite">
          {formatPlaybackRate(HOLD_PLAYBACK_RATE)}
        </div>
      ) : null}
      {/* Before `.card-media-controls` and at the same z-index, so the buttons
          paint over the bar's hit area where the two overlap. */}
      {!decorative ? <VideoProgressBar video={videoEl} label={t('media.seek')} onScrubbingChange={setScrubbing} /> : null}
      {!decorative ? (
        <div className="card-media-controls" data-rate-menu-open={rateMenuOpen || undefined}>
          {/* Both clusters fade out while playing (useControlsAutoHide, via
              `data-controls-hidden` on the surface); the progress bar and the
              hold badge stay.
              Centre: [Back 10][Play/Pause][Forward 10]. The centre button is the
              player's only play/pause control. Icon `size` here is only the
              attribute fallback: FeedCard.css scales the glyphs to the clamped
              button (57% play, 64% seek). */}
          <div className="card-media-controls-center">
            {seekable ? (
              <button
                type="button"
                className="card-media-control-btn card-media-seek-btn card-media-center-seek-btn"
                onClick={handleSeekBack}
                aria-label={t('media.back', { seconds: SEEK_JUMP_SECONDS })}
                aria-keyshortcuts="j"
              >
                <Replay10Icon size={24} />
              </button>
            ) : null}
            <button
              type="button"
              className="card-media-play-overlay"
              onClick={handlePlayButtonClick}
              aria-label={isPlaying ? t('media.pause') : t('media.play')}
            >
              {isPlaying
                ? <MediaControlIcon url={mediaPauseIconUrl} fallback={<PauseIcon size={32} />} large />
                : <MediaControlIcon url={mediaPlayIconUrl} fallback={<PlayIcon size={32} />} large />}
            </button>
            {seekable ? (
              <button
                type="button"
                className="card-media-control-btn card-media-seek-btn card-media-center-seek-btn"
                onClick={handleSeekForward}
                aria-label={t('media.forward', { seconds: SEEK_JUMP_SECONDS })}
                aria-keyshortcuts="l"
              >
                <Forward10Icon size={24} />
              </button>
            ) : null}
          </div>
          {/* Bottom-right corner: [Speed][CC][Volume]. */}
          <div className="card-media-controls-corner">
            <div className="card-media-controls-secondary">
              {/* `isActive`, not `isPlaying`: a manual pause must not close the menu. */}
              <PlaybackRateMenu
                rate={rate}
                onSelect={setRate}
                isActive={isActive}
                open={rateMenuOpen}
                onOpenChange={setRateMenuOpen}
              />
              {hasCaptions ? (
                <button
                  type="button"
                  className="card-media-control-btn card-media-captions-btn"
                  onClick={handleToggleCaptions}
                  aria-label={t('media.captions')}
                  aria-keyshortcuts="c"
                  aria-pressed={captionsEnabled}
                >
                  <CaptionsIcon size={20} />
                </button>
              ) : null}
            </div>
            <button
              type="button"
              className="card-media-control-btn card-media-mute-btn"
              onClick={handleToggleMute}
              aria-label={isMuted ? t('media.unmute') : t('media.mute')}
            >
              {isMuted
                ? <MediaControlIcon url={mediaMutedIconUrl} fallback={<VolumeOffIcon size={20} />} />
                : <VolumeOnIcon size={20} />}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function CardMedia({
  media,
  preload = false,
  postId,
  decorative = false,
  onFirstPlay,
  onMuteToggle,
}: CardMediaProps) {
  const videoIdScope = useVideoIdScope()
  if (media.length === 0) return null

  return (
    <div className={`card-media ${media.length > 1 ? 'card-media-grid' : ''}`}>
      {media.map((item, idx) => {
        if (item.type === 'video' || isHlsUrl(item.url)) {
          const video = resolveVideoMedia(item)
          if (!video.src) return null
          return (
            <VideoPlayer
              key={idx}
              src={video.src}
              fallbackSrc={video.fallbackSrc}
              poster={video.poster}
              captions={video.captions}
              preload={preload}
              videoId={scopedVideoId(videoIdScope, postId, idx)}
              decorative={decorative}
              onFirstPlay={onFirstPlay}
              onMuteToggle={onMuteToggle}
            />
          )
        }
        return <CardImage key={idx} item={item} preload={preload} />
      })}
    </div>
  )
}

/**
 * One image cell. Which srcset candidate the browser fetches depends on the
 * cell's rendered width, and that varies by surface (compact cards to the
 * 960px player) and by brand (cards are sized through CSS variables), so the
 * cell is measured rather than guessed. The image waits for the first
 * measurement, which lands before paint, so it is fetched once; a ResizeObserver
 * keeps sizes current. A cell that measures 0 (not laid out) falls back to the
 * feed column's sizes.
 */
function CardImage({ item, preload }: { item: MediaItem; preload: boolean }) {
  const cellRef = useRef<HTMLDivElement>(null)
  const srcSet = buildImageSrcSet(item)
  const [cellWidth, setCellWidth] = useState<number | null>(srcSet ? null : 0)

  useLayoutEffect(() => {
    const cell = cellRef.current
    if (!srcSet || !cell) return
    const measure = () => {
      const width = Math.round(cell.getBoundingClientRect().width)
      setCellWidth((previous) => (previous === width ? previous : width))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(cell)
    return () => observer.disconnect()
  }, [srcSet])

  // Known dimensions reserve the image's box before it loads, so the card
  // below does not shift when it arrives.
  const hasSize = Boolean(item.width && item.height && item.width > 0 && item.height > 0)

  return (
    <div ref={cellRef} className="card-media-image">
      {cellWidth === null ? null : (
        <img
          src={item.url}
          srcSet={srcSet}
          sizes={srcSet ? (cellWidth > 0 ? `${cellWidth}px` : IMAGE_SIZES_ATTR) : undefined}
          width={hasSize ? item.width ?? undefined : undefined}
          height={hasSize ? item.height ?? undefined : undefined}
          alt=""
          loading={preload ? 'eager' : 'lazy'}
          className="card-media-img"
        />
      )}
    </div>
  )
}
